import { create } from 'zustand'
import { Product, ProductType, FunnelSize } from '@/types'

// ============================================
// PRODUCT STORE (Connected to Supabase)
// ============================================

interface ProductStore {
  products: Product[]
  isLoading: boolean
  error: string | null

  // Load from API
  fetchProducts: () => Promise<void>

  // CRUD
  getProduct: (id: string) => Product | undefined
  getProductsBySku: (sku: string) => Product | undefined
  getProductsByType: (type: ProductType) => Product[]
  createProduct: (product: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>) => Promise<Product>
  updateProduct: (id: string, data: Partial<Product>) => Promise<Product | undefined>
  deleteProduct: (id: string) => Promise<boolean>

  // Helpers
  getBaseProducts: () => Product[]
  getAccessoryProducts: () => Product[]
  getProductImage: (productId: string, size: FunnelSize) => string | undefined
}

export const useProductStore = create<ProductStore>((set, get) => ({
  products: [],
  isLoading: false,
  error: null,

  fetchProducts: async () => {
    set({ isLoading: true, error: null })
    try {
      const res = await fetch('/api/admin/products')
      if (!res.ok) throw new Error('Failed to fetch products')
      const data = await res.json()
      set({ products: data, isLoading: false })
    } catch (err) {
      set({ error: (err as Error).message, isLoading: false })
    }
  },

  getProduct: (id) => {
    return get().products.find(p => p.id === id)
  },

  getProductsBySku: (sku) => {
    return get().products.find(p => p.sku === sku)
  },

  getProductsByType: (type) => {
    return get().products.filter(p => p.type === type)
  },

  createProduct: async (data) => {
    const res = await fetch('/api/admin/products', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })

    if (!res.ok) {
      const err = await res.json()
      throw new Error(err.error || 'Failed to create product')
    }

    const newProduct = await res.json()
    set(state => ({ products: [newProduct, ...state.products] }))
    return newProduct
  },

  updateProduct: async (id, data) => {
    const res = await fetch(`/api/admin/products/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    })

    if (!res.ok) {
      const err = await res.json()
      throw new Error(err.error || 'Failed to update product')
    }

    const updated = await res.json()
    set(state => ({
      products: state.products.map(p => p.id === id ? updated : p)
    }))
    return updated
  },

  deleteProduct: async (id) => {
    const res = await fetch(`/api/admin/products/${id}`, {
      method: 'DELETE',
    })

    if (!res.ok) return false

    set(state => ({
      products: state.products.filter(p => p.id !== id)
    }))
    return true
  },

  // Helpers
  getBaseProducts: () => {
    return get().products.filter(p => p.type === 'base')
  },

  getAccessoryProducts: () => {
    return get().products.filter(p => p.type === 'accessory')
  },

  getProductImage: (productId, size) => {
    const product = get().getProduct(productId)
    if (!product) return undefined
    return product.images[size] || product.thumbnail
  },
}))
